import { Dialog } from '@blueprintjs/core'
import React, { useState } from 'react'
import style from './SubscriptionSuggestionDialog.scss'

interface SubscriptionSuggestionDialogProps {
  isOpen: boolean
  numberOfBots: number
  nextSubsPlan?: string
  nextSubsDuration?: string
  upgradeInProgress: boolean
  onConfirm: (duration: 'monthly' | 'yearly') => void
  onKeepCurrent: () => void
}

export const SubscriptionSuggestionDialog: React.FC<SubscriptionSuggestionDialogProps> = ({
  isOpen,
  numberOfBots,
  nextSubsPlan,
  nextSubsDuration,
  upgradeInProgress,
  onConfirm,
  onKeepCurrent
}) => {
  const [duration, setDuration] = useState<'monthly' | 'yearly'>('monthly')

  const price = duration === 'monthly' ? 45 : 432

  return (
    <Dialog
      isOpen={isOpen}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      icon="info-sign"
      title="Subscription Suggestion"
      className={style.dialogContainer}
    >
      <div className={style.bodyContainer}>
        <div className={style.scrollableContent}>
          <div className={style.banner}>
            <p>
              You have originally opted for a <strong>{nextSubsPlan || 'Starter'}</strong> plan on a <strong>{nextSubsDuration || 'monthly'}</strong> basis after Trial ends  .
            </p>
            <p>
              We noticed that you are currently managing <strong>{numberOfBots} bots</strong>. The Starter plan supports a maximum of 3 bots only. To continue using all your bots, we suggest upgrading to the <strong>Professional Plan</strong>.
            </p>
          </div>

          <div className={style.cardsContainer}>
            {/* Left: Professional Plan details Card */}
            <div className={style.leftCard}>
              <div className={style.cardHeader}>
                <h2>Professional</h2>
                <span className={style.badge}>5 Bots Included</span>
              </div>

              <h3 className={style.priceTitle}>
                ${duration === 'monthly' ? '45' : '36'}/month
                <span className={style.priceSubtext}>
                  {duration === 'monthly' ? '' : '(billed annually at $432)'}
                </span>
              </h3>

              <div className={style.listsFlex}>
                {/* Sub-column 1: Includes & Channels */}
                <div className={style.listCol1}>
                  <div className={style.listSection}>
                    <strong>Includes:</strong>
                    <ul>
                      <li>LLM Support</li>
                      <li>HITL (Human in the Loop)</li>
                      <li>Bot Analytics</li>
                    </ul>
                  </div>
                  <div className={style.listSection}>
                    <strong>Supported Channels:</strong>
                    <ul>
                      <li>WhatsApp</li>
                      <li>Web Channel</li>
                      <li>Telegram</li>
                      <li>Slack</li>
                      <li>Facebook Messenger</li>
                    </ul>
                  </div>
                </div>

                {/* Sub-column 2: Customizations */}
                <div className={style.listCol2}>
                  <div className={style.listSection}>
                    <strong>Bot Customizations:</strong>
                    <ul>
                      <li>Chat window resize & color</li>
                      <li>Chat bubble styling</li>
                      <li>Input field styling</li>
                      <li>Send button styling</li>
                      <li>Webchat logo settings</li>
                      <li>Ripple Effect</li>
                      <li>Welcome Message Bubble</li>
                      <li style={{ fontWeight: 600, color: '#106ba3' }}>xMati branding removal</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Billing and Duration Selector */}
            <div className={style.rightCard}>
              <div>
                <h3 className={style.billingTitle}>Billing Period</h3>

                <div className={style.optionsContainer}>
                  {/* Monthly Option Card */}
                  <div
                    onClick={() => setDuration('monthly')}
                    className={`${style.optionCard} ${duration === 'monthly' ? style.active : ''}`}
                  >
                    <div className={style.optionHeader}>
                      <span>Monthly</span>
                      <input
                        type="radio"
                        checked={duration === 'monthly'}
                        onChange={() => { }}
                        style={{ margin: 0 }}
                      />
                    </div>
                    <div className={style.optionPrice}>
                      $45<span> / month</span>
                    </div>
                  </div>

                  {/* Yearly Option Card */}
                  <div
                    onClick={() => setDuration('yearly')}
                    className={`${style.optionCard} ${duration === 'yearly' ? style.active : ''}`}
                  >
                    <div className={style.optionHeader}>
                      <div>
                        <span>Yearly</span>
                        <span className={style.saveBadge}>Save 20%</span>
                      </div>
                      <input
                        type="radio"
                        checked={duration === 'yearly'}
                        onChange={() => { }}
                        style={{ margin: 0 }}
                      />
                    </div>
                    <div className={style.optionPrice}>
                      $36<span> / month</span>
                    </div>
                    <div className={style.optionBilled}>$432 billed annually</div>
                  </div>
                </div>
              </div>

              <div className={style.totalSection}>
                <div className={style.totalRow}>
                  <span className={style.totalLabel}>Total Due:</span>
                  <span className={style.totalAmount}>
                    ${price}
                    <span>{duration === 'monthly' ? ' / month' : ' / year'}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Big Action Buttons */}
        <div className={style.actionsContainer}>
          <button
            className={style.upgradeBtn}
            disabled={upgradeInProgress}
            onClick={() => onConfirm(duration)}
          >
            Upgrade Plan
          </button>

          <button
            className={style.cancelBtn}
            disabled={upgradeInProgress}
            onClick={onKeepCurrent}
          >
            No, Keep current plan
          </button>
        </div>
      </div>
    </Dialog>
  )
}
